import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMasterPartnerAuth } from '../contexts/MasterPartnerAuthContext';
import { 
  Building2, User, Phone, Mail, MapPin, Save, Loader2, Home, Users, 
  Settings, LogOut, Menu, CreditCard, FileText, CheckCircle, XCircle, 
  Clock, Eye, EyeOff, AlertCircle, ScrollText, ExternalLink, Download, X,
  Upload, Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerProfile = () => {
  const navigate = useNavigate();
  const { masterPartner, logout, getAuthHeader, refreshProfile } = useMasterPartnerAuth();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Agreement status
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [loadingAgreement, setLoadingAgreement] = useState(true);
  const [agreementContent, setAgreementContent] = useState(null);
  const [showAgreementContent, setShowAgreementContent] = useState(false);
  
  // Profile form
  const [profileData, setProfileData] = useState({
    contact_person: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  
  // Bank details form
  const [bankData, setBankData] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: ''
  });
  
  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Document upload states
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    document_type: '',
    document_name: '',
    file: null
  });

  useEffect(() => {
    if (masterPartner) {
      setProfileData({
        contact_person: masterPartner.contact_person || '',
        phone: masterPartner.phone || '',
        address: masterPartner.address || '',
        city: masterPartner.city || '',
        state: masterPartner.state || '',
        pincode: masterPartner.pincode || ''
      });
      
      if (masterPartner.bank_details) {
        setBankData({
          account_holder_name: masterPartner.bank_details.account_holder_name || '',
          bank_name: masterPartner.bank_details.bank_name || '',
          account_number: masterPartner.bank_details.account_number || '',
          ifsc_code: masterPartner.bank_details.ifsc_code || '',
          branch: masterPartner.bank_details.branch || ''
        });
      }
      
      // Set documents from master partner data
      setDocuments(masterPartner.documents || []);
      
      fetchAgreementStatus();
      fetchDocumentTypes();
    }
  }, [masterPartner]);

  const fetchDocumentTypes = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/document-types`, {
        headers: getAuthHeader()
      });
      setDocumentTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching document types:', error);
      // Fallback to default types
      setDocumentTypes([
        { key: 'aadhaar_card', label: 'Aadhaar Card', required: true },
        { key: 'pan_card', label: 'PAN Card', required: true },
        { key: 'gst_certificate', label: 'GST Certificate', required: false },
        { key: 'cancelled_cheque', label: 'Cancelled Cheque', required: false },
        { key: 'company_registration', label: 'Company Registration', required: false },
        { key: 'other', label: 'Other Document', required: false }
      ]);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadFormData.file || !uploadFormData.document_type) {
      setMessage({ type: 'error', text: 'Please select a document type and file' });
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFormData.file);
      formData.append('document_type', uploadFormData.document_type);
      if (uploadFormData.document_name) {
        formData.append('document_name', uploadFormData.document_name);
      }

      await axios.post(`${API}/master-partner/portal/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ type: 'success', text: 'Document uploaded successfully' });
      setShowUploadModal(false);
      setUploadFormData({ document_type: '', document_name: '', file: null });
      
      // Refresh profile to get updated documents
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to upload document' });
    } finally {
      setUploadingDocument(false);
    }
  };

  const fetchAgreementStatus = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/agreement`, {
        headers: getAuthHeader()
      });
      setAgreementStatus(response.data);
    } catch (error) {
      console.error('Error fetching agreement status:', error);
    } finally {
      setLoadingAgreement(false);
    }
  };

  const viewAgreementContent = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/agreement/content`, {
        headers: getAuthHeader()
      });
      setAgreementContent(response.data);
      setShowAgreementContent(true);
    } catch (error) {
      console.error('Error fetching agreement content:', error);
      setMessage({ type: 'error', text: 'Failed to load agreement content' });
    }
  };

  const downloadAgreementPDF = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/agreement/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Master_Partner_Agreement_${masterPartner?.company_name?.replace(/\s+/g, '_') || 'Agreement'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading agreement:', error);
      setMessage({ type: 'error', text: 'Failed to download agreement PDF' });
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API}/master-partner/portal/profile`, profileData, {
        headers: getAuthHeader()
      });
      await refreshProfile();
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const saveBankDetails = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API}/master-partner/portal/bank-details`, bankData, {
        headers: getAuthHeader()
      });
      await refreshProfile();
      setMessage({ type: 'success', text: 'Bank details updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update bank details' });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API}/master-partner/portal/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: getAuthHeader()
      });
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/master-partner/login');
  };

  const getDocumentStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getDocumentStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold text-gray-900">Master Partner</h1>
          <p className="text-sm text-gray-500 truncate">{masterPartner?.company_name}</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link 
            to="/master-partner/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/master-partner/partners" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Users className="w-5 h-5" />
            My Partners
          </Link>
          <Link 
            to="/master-partner/commissions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <CreditCard className="w-5 h-5" />
            Commissions
          </Link>
          <Link 
            to="/master-partner/profile" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-50 text-purple-700 font-medium"
          >
            <Settings className="w-5 h-5" />
            Profile
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Profile & Settings</h2>
            <div></div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Message */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </div>
          )}

          {/* Company Info Card */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{masterPartner?.company_name}</h3>
                <p className="text-purple-100">{masterPartner?.email}</p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full ${
                    masterPartner?.status === 'approved' ? 'bg-green-500/20' : 'bg-amber-500/20'
                  }`}>
                    {masterPartner?.status}
                  </span>
                  <span>Commission: {masterPartner?.commission_percentage || 10}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Agreement Alert Banner */}
          {agreementStatus && agreementStatus.status === 'pending_acceptance' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <ScrollText className="w-6 h-6 text-amber-600" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Agreement Pending Acceptance</h4>
                    <p className="text-sm text-amber-700">Please review and accept the {agreementStatus.agreement?.title} to start recruiting partners.</p>
                  </div>
                </div>
                <Link
                  to={`/master-partner/agreement/${agreementStatus.agreement_token}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
                >
                  <ExternalLink className="w-4 h-4" />
                  Accept Agreement
                </Link>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b overflow-x-auto">
            {['profile', 'agreement', 'bank', 'documents', 'security'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium capitalize transition-colors whitespace-nowrap ${
                  activeTab === tab 
                    ? 'text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'bank' ? 'Bank Details' : tab}
              </button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                  <input
                    type="text"
                    name="contact_person"
                    value={profileData.contact_person}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={profileData.address}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    value={profileData.city}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={profileData.state}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={profileData.pincode}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Agreement Tab */}
          {activeTab === 'agreement' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Agreement Status</h3>
              
              {loadingAgreement ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : agreementStatus ? (
                <div className="space-y-6">
                  {agreementStatus.status === 'accepted' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div>
                          <h4 className="text-lg font-semibold text-green-800">Agreement Accepted</h4>
                          <p className="text-green-700">You have accepted the Master Partner Agreement.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-green-600 font-medium">Version:</span>
                          <span className="ml-2 text-green-800">{agreementStatus.version}</span>
                        </div>
                        <div>
                          <span className="text-green-600 font-medium">Accepted On:</span>
                          <span className="ml-2 text-green-800">
                            {new Date(agreementStatus.accepted_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-green-200 flex flex-wrap gap-3">
                        <button
                          onClick={viewAgreementContent}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Agreement
                        </button>
                        <button
                          onClick={downloadAgreementPDF}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </button>
                      </div>
                    </div>
                  )}

                  {agreementStatus.status === 'pending_acceptance' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Clock className="w-8 h-8 text-amber-600" />
                        <div>
                          <h4 className="text-lg font-semibold text-amber-800">Agreement Pending</h4>
                          <p className="text-amber-700">Please review and accept the agreement to start recruiting partners.</p>
                        </div>
                      </div>
                      <div className="mb-4 text-sm">
                        <span className="text-amber-600 font-medium">Agreement:</span>
                        <span className="ml-2 text-amber-800">{agreementStatus.agreement?.title} (v{agreementStatus.agreement?.version})</span>
                      </div>
                      
                      {agreementStatus.message && (
                        <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                          <p className="text-sm text-amber-800 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {agreementStatus.message}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={viewAgreementContent}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Preview Agreement
                        </button>
                        {agreementStatus.can_accept !== false && (
                          <Link
                            to={`/master-partner/agreement/${agreementStatus.agreement_token}`}
                            className="inline-flex items-center gap-2 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            <ScrollText className="w-5 h-5" />
                            Review & Accept Agreement
                          </Link>
                        )}
                      </div>
                    </div>
                  )}

                  {agreementStatus.status === 'documents_pending' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <h4 className="text-lg font-semibold text-blue-800">Documents Required</h4>
                          <p className="text-blue-700">{agreementStatus.message}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab('documents')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Documents
                      </button>
                    </div>
                  )}

                  {agreementStatus.status === 'not_sent' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <AlertCircle className="w-8 h-8 text-gray-500" />
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">Agreement Not Yet Available</h4>
                          <p className="text-gray-600">{agreementStatus.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Unable to load agreement status</p>
                </div>
              )}
            </div>
          )}

          {/* Agreement Content Modal */}
          {showAgreementContent && agreementContent && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{agreementContent.title}</h3>
                    <p className="text-sm text-gray-500">Version: {agreementContent.version}</p>
                  </div>
                  <button
                    onClick={() => setShowAgreementContent(false)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: agreementContent.content }}
                  />
                </div>
                <div className="p-4 border-t flex justify-end gap-3">
                  {agreementContent.accepted && (
                    <button
                      onClick={downloadAgreementPDF}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                  )}
                  <button
                    onClick={() => setShowAgreementContent(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bank' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Bank Account Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name *</label>
                  <input
                    type="text"
                    name="account_holder_name"
                    value={bankData.account_holder_name}
                    onChange={handleBankChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={bankData.bank_name}
                    onChange={handleBankChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                  <input
                    type="text"
                    name="account_number"
                    value={bankData.account_number}
                    onChange={handleBankChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code *</label>
                  <input
                    type="text"
                    name="ifsc_code"
                    value={bankData.ifsc_code}
                    onChange={handleBankChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                  <input
                    type="text"
                    name="branch"
                    value={bankData.branch}
                    onChange={handleBankChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={saveBankDetails}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Bank Details
                </button>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Submitted Documents</h3>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Upload Document
                </button>
              </div>
              
              {masterPartner?.documents?.length > 0 ? (
                <div className="space-y-4">
                  {masterPartner.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{doc.document_name || doc.document_type}</p>
                          <p className="text-sm text-gray-500">{doc.file_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getDocumentStatusColor(doc.status)}`}>
                          {getDocumentStatusIcon(doc.status)}
                          {doc.status}
                        </span>
                        <a
                          href={doc.file_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-700"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No documents uploaded yet</p>
                  <p className="text-sm mt-2">Upload your documents to complete your profile</p>
                </div>
              )}
            </div>
          )}

          {/* Document Upload Modal */}
          {showUploadModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">Upload Document</h3>
                    <button
                      onClick={() => {
                        setShowUploadModal(false);
                        setUploadFormData({ document_type: '', document_name: '', file: null });
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <form onSubmit={handleDocumentUpload} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                    <select
                      value={uploadFormData.document_type}
                      onChange={(e) => setUploadFormData({...uploadFormData, document_type: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select document type</option>
                      {documentTypes.map((type) => (
                        <option key={type.key} value={type.key}>
                          {type.label} {type.required && '*'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Name (Optional)</label>
                    <input
                      type="text"
                      value={uploadFormData.document_name}
                      onChange={(e) => setUploadFormData({...uploadFormData, document_name: e.target.value})}
                      placeholder="e.g., My Aadhaar Card"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        id="document-file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setUploadFormData({...uploadFormData, file: e.target.files[0]})}
                      />
                      <label htmlFor="document-file" className="cursor-pointer">
                        {uploadFormData.file ? (
                          <div className="text-purple-600">
                            <FileText className="w-10 h-10 mx-auto mb-2" />
                            <p className="font-medium">{uploadFormData.file.name}</p>
                            <p className="text-sm text-gray-500 mt-1">Click to change file</p>
                          </div>
                        ) : (
                          <div className="text-gray-500">
                            <Upload className="w-10 h-10 mx-auto mb-2" />
                            <p className="font-medium">Click to upload</p>
                            <p className="text-sm mt-1">PDF, JPG, PNG (max 5MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUploadModal(false);
                        setUploadFormData({ document_type: '', document_name: '', file: null });
                      }}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={uploadingDocument || !uploadFormData.file || !uploadFormData.document_type}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                    >
                      {uploadingDocument && <Loader2 className="w-4 h-4 animate-spin" />}
                      Upload Document
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h3>
              <div className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <button
                  onClick={changePassword}
                  disabled={saving || !passwordData.current_password || !passwordData.new_password}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Change Password
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MasterPartnerProfile;
